// ** React Imports
import { Fragment, useState, useEffect, useCallback } from "react"
// ** Third Party Components
import { useForm, Controller, useFieldArray } from "react-hook-form"
import { ArrowLeft, ArrowRight, X, Plus } from "react-feather"
// ** Utils
import { getAllFromController } from "@lib/api"
import { validateEmail, validatePhone } from "@lib/util"
// ** Reactstrap Imports
import {
  Label,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  Input,
  InputGroup,
  InputGroupText
} from "reactstrap"
// ** Styles
import "@styles/react/libs/react-select/_react-select.scss"
import "@styles/react/libs/flatpickr/flatpickr.scss"
// ** Store Vars
import { useDispatch, useSelector } from "react-redux"
import { setFormData } from "@store/stores/person"

const ContactInfo = ({ stepper }) => {
  const [types, setTypes] = useState({})
  // ** Store Vars
  const store = useSelector((state) => state.persons)

  const defaultValues = {}
  if (store.selectedPerson) {
    defaultValues.emails = store.selectedPerson.emails.map((email) => ({
      id: email.id,
      value: email.address,
      typeId: email.typeId
    }))
    defaultValues.phones = store.selectedPerson.phones.map((phone) => ({
      id: phone.id,
      value: phone.number,
      typeId: phone.typeId
    }))
  }

  // ** Hooks
  const dispatch = useDispatch()
  const {
    control,
    setError,
    handleSubmit,
    clearErrors,
    formState: { errors }
  } = useForm({ defaultValues })
  const {
    fields: fieldsEmail,
    append: appendEmail,
    remove: removeEmail
  } = useFieldArray({
    control,
    name: "emails"
  })
  const {
    fields: fieldsPhone,
    append: appendPhone,
    remove: removePhone
  } = useFieldArray({
    control,
    name: "phones"
  })
  const fetchTypes = useCallback(async () => {
    const email = await getAllFromController("Emails/Types", "crm")
    const phone = await getAllFromController("Phones/Types", "crm")
    setTypes({ email: email.data, phone: phone.data })
  }, [])
  useEffect(() => {
    fetchTypes()
  }, [fetchTypes])

  // ** Functions
  const onSubmit = (data) => {
    // Validations
    let error = false
    for (const key in data) {
      data[key].map((item, index) => {
        const value = item.value
        if (value === "" || value === null || value === undefined) {
          error = true
          setError(`${key}.${index}`, {
            type: "manual",
            message: `${key.slice(0, -1)} ${index + 1} : required`
          })
        } else {
          if (
            (key === "emails" && !validateEmail(value)) ||
            (key === "phones" && !validatePhone(value))
          ) {
            error = true
            setError(`${key}.${index}`, {
              type: "manual",
              message: `${key.slice(0, -1)} ${index + 1} : not valid`
            })
          }
        }
      })
    }
    // reshape and post
    if (!error) {
      const cleanData = {
        emails: data.emails.map((email) => ({
          id: email.id,
          address: email.value,
          entityId: store.selectedPerson?.id,
          typeId: email.typeId
        })),
        phones: data.phones.map((phone) => ({
          id: phone.id,
          number: phone.value,
          entityId: store.selectedPerson?.id,
          typeId: phone.typeId
        }))
      }
      dispatch(
        setFormData({
          ...store.formData,
          contactInfos: cleanData
        })
      )
      stepper.next()
    }
  }

  return (
    <Fragment>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="content-header">
            <h5 className="mb-0">Contact information</h5>
            <small>Email and phone used to reach the person</small>
          </div>
          <div className="d-flex gap-2">
            <Button
              type="button"
              color="primary"
              className="btn-prev"
              onClick={() => stepper.previous()}
            >
              <ArrowLeft size={14} className="align-middle me-sm-25 me-0"></ArrowLeft>
              <span className="align-middle d-sm-inline-block d-none">Previous</span>
            </Button>
            <Button type="submit" color="primary" className="btn-next">
              <span className="align-middle d-sm-inline-block d-none">Next</span>
              <ArrowRight size={14} className="align-middle ms-sm-25 ms-0" />
            </Button>
          </div>
        </div>
        <hr />

        <h6>Email(s)</h6>
        {fieldsEmail.map((item, index) => {
          return (
            <Row key={item.id} className="item-wrapper mb-3">
              <Col md="10">
                <Label className="form-label">Email address {index + 1}</Label>
                <InputGroup className="input-group">
                  <InputGroupText>
                    <Controller
                      control={control}
                      name={`emails.${index}.typeId`}
                      render={({ field }) => (
                        <Input {...field} type="select" className="border-0" role="button">
                          {types.email?.map((type) => (
                            <option key={type.id} value={parseInt(type.id)}>
                              {type.name}
                            </option>
                          ))}
                        </Input>
                      )}
                    />
                  </InputGroupText>
                  <Controller
                    control={control}
                    name={`emails.${index}.value`}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="email"
                        placeholder="email@domain.com"
                        onFocus={() => clearErrors(`emails.${index}`)}
                        invalid={errors.emails?.[index] && true}
                      />
                    )}
                  />
                </InputGroup>
              </Col>
              <Col md="2" className="d-flex align-items-center">
                <Button
                  outline
                  color="danger"
                  className="btn-icon rounded-circle"
                  onClick={() => removeEmail(index)}
                >
                  <X size={14} />
                </Button>
              </Col>
            </Row>
          )
        })}
        <div>
          <Button
            className="btn-icon"
            color="outline-primary"
            onClick={() => {
              clearErrors()
              appendEmail({ value: "", typeId: 2 })
            }}
          >
            <Plus size={14} />
            <span className="align-middle ms-25">Add</span>
          </Button>
        </div>
        <hr />

        {errors.emails && (
          <Card color="danger" outline>
            <CardBody>
              <ul>
                {errors.emails?.map((error, index) => (
                  <li className="text-danger" key={index}>
                    {error?.message}
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}

        <h6>Phone number(s)</h6>
        {fieldsPhone.map((item, index) => (
          <Row key={item.id} className="item-wrapper mb-3">
            <Col md="10">
              <Label className="form-label">Phone number {index + 1}</Label>
              <InputGroup className="input-group">
                <InputGroupText>
                  <Controller
                    control={control}
                    name={`phones.${index}.typeId`}
                    render={({ field }) => (
                      <Input {...field} type="select" className="border-0" role="button">
                        {types.phone?.map((type) => (
                          <option key={type.id} value={parseInt(type.id)}>
                            {type.name}
                          </option>
                        ))}
                      </Input>
                    )}
                  />
                </InputGroupText>

                <Controller
                  control={control}
                  name={`phones.${index}.value`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      placeholder="0211597536"
                      onFocus={() => clearErrors(`phones.${index}`)}
                      invalid={errors.phones?.[index] && true}
                    />
                  )}
                />
              </InputGroup>
            </Col>
            <Col md="2" className="d-flex align-items-center">
              <Button
                outline
                color="danger"
                className="btn-icon rounded-circle"
                onClick={() => removePhone(index)}
              >
                <X size={14} />
              </Button>
            </Col>
          </Row>
        ))}
        <div>
          <Button
            className="btn-icon"
            color="outline-primary"
            onClick={() => {
              clearErrors()
              appendPhone({ value: "", typeId: 3 })
            }}
          >
            <Plus size={14} />
            <span className="align-middle ms-25">Add</span>
          </Button>
        </div>
        <hr />

        {errors.phones && (
          <Card color="danger" outline>
            <CardBody>
              <ul>
                {errors.phones?.map((error, index) => (
                  <li className="text-danger" key={index}>
                    {error?.message}
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}
      </Form>
    </Fragment>
  )
}

export default ContactInfo
